/**
 * Mock API service for local development
 */
import { calculateEOQ, calculateReorderPoint, calculateSafetyStock } from './inventory-utils';

/**
 * Process inventory data and generate optimization recommendations
 * @param {Object} data - Inventory data and parameters
 * @returns {Object} - Optimization results
 */
export const processInventoryData = async (data) => {
  // Simulate API delay with safe timeout
  const delay = 2000; // Fixed delay
  await new Promise(resolve => setTimeout(resolve, delay));
  
  const { skuCount, warehouseCount, seasonalFactors, promotionalEvents } = data;
  
  // Generate mock results
  const results = {
    summary: {
      totalSkus: parseInt(skuCount) || 1000,
      warehouseCount: parseInt(warehouseCount) || 1,
      forecastAccuracy: `${Math.floor(80 + Math.random() * 15)}%`,
      potentialSavings: `$${Math.floor(300000 + Math.random() * 300000).toLocaleString()}`,
      stockoutReduction: `${Math.floor(30 + Math.random() * 30)}%`
    },
    recommendations: {
      highPriority: [],
      seasonalAdjustments: []
    }
  };
  
  // Generate high priority SKU recommendations
  for (let i = 0; i < 5; i++) {
    const sku = `SKU-${Math.floor(1000 + Math.random() * 9000)}`;
    const warehouse = `WH-${String(Math.floor(Math.random() * warehouseCount) + 1).padStart(2, '0')}`;
    const annualDemand = Math.floor(1000 + Math.random() * 5000);
    const orderCost = 50;
    const holdingCost = 10;
    const leadTimeDays = Math.floor(3 + Math.random() * 14);
    const stdDevDemand = Math.floor(5 + Math.random() * 20);
    const serviceLevel = 1.96; // ~95% service level
    
    const eoq = Math.floor(calculateEOQ(annualDemand, orderCost, holdingCost));
    const safetyStock = Math.floor(calculateSafetyStock(serviceLevel, leadTimeDays, stdDevDemand));
    const leadTimeDemand = Math.floor((annualDemand / 365) * leadTimeDays);
    const reorderPoint = Math.floor(calculateReorderPoint(leadTimeDemand, safetyStock));
    
    results.recommendations.highPriority.push({
      sku,
      warehouse,
      reorderPoint,
      eoq,
      safetyStock
    });
  }
  
  // Generate seasonal adjustments if applicable
  if (seasonalFactors) {
    const seasons = ['Spring', 'Summer', 'Fall', 'Winter', 'Holiday'];
    const skuGroups = ['Seasonal Products', 'Electronics', 'Apparel', 'Home Goods', 'Outdoor Equipment'];
    
    for (let i = 0; i < 5; i++) {
      results.recommendations.seasonalAdjustments.push({
        season: seasons[i % seasons.length],
        skuGroup: skuGroups[i % skuGroups.length],
        adjustmentFactor: (1 + (Math.random() * 0.8 + 0.2)).toFixed(2)
      });
    }
  }
  
  // Add promotional adjustments if applicable
  if (promotionalEvents) {
    results.recommendations.promotionalAdjustments = [
      { event: 'Black Friday', skuGroup: 'Electronics', adjustmentFactor: (1.8 + Math.random() * 0.5).toFixed(2) },
      { event: 'Back to School', skuGroup: 'Office Supplies', adjustmentFactor: (1.4 + Math.random() * 0.3).toFixed(2) },
      { event: 'Summer Sale', skuGroup: 'Outdoor Equipment', adjustmentFactor: (1.3 + Math.random() * 0.3).toFixed(2) }
    ];
  }
  
  return results;
};

// Function to analyze requirements and generate appropriate proposal
const generateProposalTemplate = (requirements) => {
  // Analyze requirements to determine project type
  const lowerReq = requirements.toLowerCase();
  
  // Check for inventory management keywords
  if (lowerReq.includes('inventory') || lowerReq.includes('stock') || lowerReq.includes('order')) {
    return generateInventorySystemProposal(requirements);
  }
  
  // Check for chatbot keywords
  else if (lowerReq.includes('chatbot') || lowerReq.includes('chat') || lowerReq.includes('conversation')) {
    return generateChatbotProposal(requirements);
  }
  
  // Check for data analysis keywords
  else if (lowerReq.includes('data') || lowerReq.includes('analytics') || lowerReq.includes('insight')) {
    return generateDataAnalyticsProposal(requirements);
  }
  
  // Default to a general AI solution proposal
  else {
    return generateGeneralAIProposal(requirements);
  }
};

// Inventory Management System Proposal
const generateInventorySystemProposal = (requirements) => {
  // Extract key information from requirements
  const lowerReq = requirements.toLowerCase();
  
  // Determine product count - check for SKUs, products, items, inventory items
  let productCount = 50; // Default
  const skuMatch = lowerReq.match(/(\d+[,\d]*)\s*(?:sku|skus)/i);
  const productMatch = lowerReq.match(/(\d+[,\d]*)\s*(?:product|products)/i);
  const itemMatch = lowerReq.match(/(\d+[,\d]*)\s*(?:item|items)/i);
  const inventoryMatch = lowerReq.match(/(\d+[,\d]*)\s*(?:inventory item|inventory items)/i);
  
  // Use the first match found, prioritizing SKUs
  if (skuMatch) {
    productCount = parseInt(skuMatch[1].replace(/,/g, ''));
  } else if (productMatch) {
    productCount = parseInt(productMatch[1].replace(/,/g, ''));
  } else if (itemMatch) {
    productCount = parseInt(itemMatch[1].replace(/,/g, ''));
  } else if (inventoryMatch) {
    productCount = parseInt(inventoryMatch[1].replace(/,/g, ''));
  }
  
  // Determine warehouse count
  let warehouseCount = 1; // Default
  if (lowerReq.includes('one warehouse') || lowerReq.includes('1 warehouse') || lowerReq.includes('single warehouse')) {
    warehouseCount = 1;
  } else if (lowerReq.match(/(\d+)\s*warehouses?/i)) {
    warehouseCount = parseInt(lowerReq.match(/(\d+)\s*warehouses?/i)[1]);
  }
  
  // Determine if spreadsheet-based
  const isSpreadsheetBased = lowerReq.includes('spreadsheet') || lowerReq.includes('excel') || lowerReq.includes('csv');
  
  // Determine if integration is needed
  const needsIntegration = !lowerReq.includes('no integration') && !lowerReq.includes('does not need to be integrated') && 
                          !lowerReq.includes('without integration') && lowerReq.includes('integrat');
  
  // Calculate appropriate costs based on requirements with better scaling
  // Base cost increases with project size
  let baseDevCost;
  if (productCount <= 100) baseDevCost = 5000;
  else if (productCount <= 500) baseDevCost = 10000;
  else if (productCount <= 1000) baseDevCost = 15000;
  else if (productCount <= 5000) baseDevCost = 25000;
  else baseDevCost = 40000;
  
  // Per product cost decreases with volume
  let perProductCost;
  if (productCount <= 100) perProductCost = 50;
  else if (productCount <= 500) perProductCost = 30;
  else if (productCount <= 1000) perProductCost = 20;
  else if (productCount <= 5000) perProductCost = 10;
  else perProductCost = 5;
  
  // Factors for complexity
  const warehouseFactor = Math.min(2.5, 1 + (warehouseCount - 1) * 0.3); // Scales with warehouse count but caps at 2.5x
  const integrationFactor = needsIntegration ? (lowerReq.includes('pos') ? 1.8 : 1.5) : 1; // POS integration is more complex
  const complexityFactor = isSpreadsheetBased ? 0.8 : 1.2;
  
  // Additional factors for enterprise features
  const reportingFactor = lowerReq.includes('report') ? 1.3 : 1;
  const detailedFactor = lowerReq.includes('detailed') ? 1.2 : 1;
  
  // Calculate development cost with diminishing returns for large product counts
  const productCostContribution = productCount <= 1000 ? 
    (productCount * perProductCost) : 
    (1000 * perProductCost + (productCount - 1000) * perProductCost * 0.5);
  
  // Apply Self Service Build Wizard discount
  const wizardDiscount = 0.65; // 35% discount with wizard
  
  const devCost = Math.round(baseDevCost + (productCostContribution * warehouseFactor * integrationFactor * complexityFactor * reportingFactor * detailedFactor));
  const discountedDevCost = Math.round(devCost * wizardDiscount);
  const minDevCost = Math.max(3000, Math.round(discountedDevCost * 0.8));
  const maxDevCost = Math.round(discountedDevCost * 1.2);
  
  // Monthly costs with better scaling
  let monthlyBaseCost;
  if (productCount <= 100) monthlyBaseCost = 200;
  else if (productCount <= 500) monthlyBaseCost = 400;
  else if (productCount <= 1000) monthlyBaseCost = 800;
  else if (productCount <= 5000) monthlyBaseCost = 1500;
  else monthlyBaseCost = 3000;
  
  // Monthly per-product cost decreases with volume
  let monthlyPerProductCost;
  if (productCount <= 100) monthlyPerProductCost = 2;
  else if (productCount <= 500) monthlyPerProductCost = 1.5;
  else if (productCount <= 1000) monthlyPerProductCost = 1;
  else if (productCount <= 5000) monthlyPerProductCost = 0.5;
  else monthlyPerProductCost = 0.2;
  
  const monthlyCost = Math.round(monthlyBaseCost + (productCount * monthlyPerProductCost * warehouseFactor * complexityFactor * reportingFactor));
  const minMonthlyCost = Math.max(100, Math.round(monthlyCost * 0.8));
  const maxMonthlyCost = Math.round(monthlyCost * 1.2);
  
  // Determine appropriate solution components based on requirements
  const uiComponents = [];
  uiComponents.push('Simple web dashboard for inventory management');
  if (productCount > 20) uiComponents.push('Bulk import/export functionality');
  if (isSpreadsheetBased) uiComponents.push('Spreadsheet template for data input');
  
  const aiComponents = [];
  aiComponents.push('Basic demand forecasting based on historical data');
  aiComponents.push('Automated reorder point calculation');
  if (productCount > 30) aiComponents.push('Inventory optimization suggestions');
  
  // Determine appropriate timeline based on project size and complexity
  let discoveryWeeks, designWeeks, developmentWeeks, testingWeeks, deploymentWeeks;
  
  if (productCount <= 100) {
    discoveryWeeks = 1;
    designWeeks = 1;
    developmentWeeks = Math.max(2, Math.min(4, Math.ceil(productCount / 25)));
    testingWeeks = 1;
    deploymentWeeks = 1;
  } else if (productCount <= 500) {
    discoveryWeeks = 2;
    designWeeks = 2;
    developmentWeeks = Math.max(4, Math.min(6, Math.ceil(productCount / 100)));
    testingWeeks = 2;
    deploymentWeeks = 1;
  } else if (productCount <= 1000) {
    discoveryWeeks = 2;
    designWeeks = 3;
    developmentWeeks = Math.max(6, Math.min(8, Math.ceil(productCount / 150)));
    testingWeeks = 3;
    deploymentWeeks = 2;
  } else {
    discoveryWeeks = 3;
    designWeeks = 4;
    developmentWeeks = Math.max(8, Math.min(12, Math.ceil(productCount / 200)));
    testingWeeks = 4;
    deploymentWeeks = 2;
  }
  
  // Adjust for complexity factors
  if (warehouseCount > 2) {
    developmentWeeks += 1;
    testingWeeks += 1;
  }
  
  if (needsIntegration) {
    discoveryWeeks += 1;
    developmentWeeks += 2;
    testingWeeks += 1;
  }
  
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a streamlined inventory management solution tailored for small businesses that leverages AI to optimize your inventory levels and automate ordering processes.

## Solution Architecture

We recommend building a ${productCount > 500 ? 'comprehensive' : 'lightweight'} inventory management system using our Self Service Build Wizard to accelerate development with the following components:

1. **User Interface**
   ${uiComponents.map(item => `- ${item}`).join('\n   ')}

2. **Core Functionality**
   - Inventory tracking for ${productCount.toLocaleString()} ${productCount > 500 ? 'SKUs' : 'products'}
   - Purchase order generation based on inventory levels
   - Historical sales data analysis
   ${warehouseCount > 1 ? `- Support for ${warehouseCount} warehouse locations` : '- Single warehouse inventory management'}
   ${needsIntegration ? '- Integration with your existing POS system' : ''}

3. **AI-Powered Features**
   ${aiComponents.map(item => `- ${item}`).join('\n   ')}
   ${productCount > 500 ? '- Advanced inventory optimization across multiple locations' : ''}
   ${productCount > 1000 ? '- Machine learning for seasonal demand prediction' : ''}

4. **Data Management**
   ${isSpreadsheetBased ? '- Spreadsheet-based data storage with automated processing' : productCount > 500 ? '- Scalable database architecture for large inventory catalogs' : '- Simple database for inventory records'}
   - Secure cloud backup of all inventory data
   - ${lowerReq.includes('report') ? 'Comprehensive reporting and analytics dashboard' : 'Export functionality for reporting'}
   ${productCount > 1000 ? '- Data warehousing for historical analysis' : ''}

## Key Features

- **Smart Inventory Tracking**: Keep accurate count of your ${productCount.toLocaleString()} ${productCount > 500 ? 'SKUs' : 'products'}
- **Automated Purchase Orders**: System generates purchase order recommendations based on inventory levels
- **Predictive Ordering**: AI analyzes sales patterns to recommend optimal order quantities
${productCount > 500 ? '- **Multi-Warehouse Management**: Synchronized inventory across all warehouse locations' : '- **Streamlined Operations**: Designed for efficient inventory management'}
${needsIntegration ? '- **POS Integration**: Seamless connection with your existing point-of-sale system' : ''}

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | ${discoveryWeeks} week | Requirements gathering and process mapping |
| Design | ${designWeeks} week | Solution design and data structure planning |
| Development | ${developmentWeeks} weeks | Implementation of core features and AI components |
| Testing | ${testingWeeks} week | Quality assurance and user acceptance testing |
| Deployment | ${deploymentWeeks} ${deploymentWeeks === 1 ? 'week' : 'weeks'} | Setup, data import, and training |

## Cost Estimate

Based on your specific requirements for ${productCount} products in ${warehouseCount} warehouse location${warehouseCount > 1 ? 's' : ''}, we estimate the following costs:

### Development Costs
- One-time setup and implementation: $${minDevCost.toLocaleString()} - $${maxDevCost.toLocaleString()}

### Ongoing Costs (Monthly)
- Cloud hosting and AI processing: $${minMonthlyCost} - $${maxMonthlyCost}
- Includes maintenance, updates, and support

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating infrastructure setup and configuration
- Providing pre-built components for common AI functionalities
- Streamlining integration with AWS services
- Reducing manual coding requirements
- Enabling rapid prototyping and iteration

## Assumptions

This proposal is based on the following assumptions:
- Inventory tracking for ${productCount} products
- ${warehouseCount === 1 ? 'Single warehouse location' : `${warehouseCount} warehouse locations`}
- ${isSpreadsheetBased ? 'Spreadsheet-based data input and export' : 'Simple database storage'}
- ${needsIntegration ? 'Basic integration with your existing systems' : 'No integration with external systems required'}
- Standard security for small business applications
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a brief discovery call to confirm your requirements
2. Review your current inventory process and historical data
3. Finalize the project scope and timeline
4. Begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// Chatbot Proposal
const generateChatbotProposal = (requirements) => {
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose the following AI chatbot solution.

## Solution Architecture

We recommend building a chatbot using AWS Bedrock and our Self Service Build Wizard to accelerate development with the following components:

1. **User Interface Layer**
   - Web interface with responsive design
   - Mobile compatibility
   - Omnichannel support (website, mobile app, social media)

2. **Application Layer**
   - AWS Lambda for serverless processing
   - API Gateway for secure endpoints
   - Amazon Connect for voice integration (optional)

3. **AI/ML Layer**
   - AWS Bedrock for natural language processing
   - Amazon Comprehend for sentiment analysis
   - Custom training for domain-specific knowledge

4. **Data Layer**
   - Amazon DynamoDB for customer data
   - Amazon S3 for conversation logs
   - Amazon RDS for structured data

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | 2 weeks | Requirements gathering and analysis |
| Design | 3 weeks | Solution architecture and conversation flow design |
| Development | 8 weeks | Implementation of all components |
| Testing | 3 weeks | Quality assurance and user acceptance testing |
| Deployment | 1 week | Production deployment and monitoring setup |

## Cost Estimate

Based on the requirements provided, we estimate the following costs:

### Development Costs
- Discovery & Design: $15,000 - $20,000
- Implementation: $40,000 - $60,000
- Testing & Deployment: $10,000 - $15,000

### Ongoing Costs (Monthly)
- AWS Infrastructure: $500 - $1,500
- Maintenance & Support: $2,000 - $3,000
- Model Updates & Training: $1,000 - $2,000

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating chatbot infrastructure setup and configuration
- Providing pre-built conversation flows and intent recognition
- Streamlining integration with AWS Bedrock and other AI services
- Reducing manual coding requirements by up to 60%
- Enabling rapid prototyping and iteration of conversation designs

## Assumptions

This proposal is based on the following assumptions:
- The chatbot will handle approximately 1,000 customer inquiries per day
- Integration with existing customer service systems via API
- No more than 5 custom intents beyond standard customer inquiries
- Standard security requirements (no special compliance needs)
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a detailed discovery workshop
2. Finalize requirements and scope
3. Sign statement of work
4. Begin design phase

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// Data Analytics Proposal
const generateDataAnalyticsProposal = (requirements) => {
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a comprehensive data analytics solution powered by AI to transform your raw data into actionable business insights.

## Solution Architecture

We recommend building a data analytics platform using our Self Service Build Wizard to accelerate development with the following components:

1. **Data Collection Layer**
   - Data connectors for various sources
   - ETL pipelines using AWS Glue
   - Real-time data streaming with Amazon Kinesis

2. **Processing Layer**
   - Amazon EMR for big data processing
   - AWS Lambda for serverless processing
   - Amazon SageMaker for machine learning

3. **Analytics Layer**
   - Custom ML models for predictive analytics
   - Amazon QuickSight for visualization
   - Natural language query capabilities

4. **Storage Layer**
   - Amazon S3 for data lake
   - Amazon Redshift for data warehouse
   - Amazon DynamoDB for operational data

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | 3 weeks | Data assessment, requirements gathering, and use case definition |
| Design | 4 weeks | Data architecture, pipeline design, and analytics framework |
| Development | 12 weeks | Implementation of data pipelines, models, and dashboards |
| Testing | 4 weeks | Quality assurance, model validation, and performance testing |
| Deployment | 2 weeks | Production deployment and knowledge transfer |

## Cost Estimate

Based on the requirements provided, we estimate the following costs:

### Development Costs
- Discovery & Design: $25,000 - $35,000
- Implementation: $60,000 - $90,000
- Testing & Deployment: $15,000 - $25,000

### Ongoing Costs (Monthly)
- AWS Infrastructure: $1,000 - $3,000
- Maintenance & Support: $3,000 - $5,000
- Model Updates & Training: $1,500 - $3,000

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating data pipeline setup and configuration
- Providing pre-built analytics components and visualization templates
- Streamlining integration with AWS analytics services
- Reducing manual coding requirements by up to 50%
- Enabling rapid prototyping and iteration of analytics models

## Assumptions

This proposal is based on the following assumptions:
- Data volume of up to 1TB initially
- Integration with up to 5 data sources
- Up to 10 custom dashboards and reports
- Standard security and compliance requirements
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a data discovery workshop
2. Conduct a proof of concept with a subset of your data
3. Finalize requirements and analytics use cases
4. Sign statement of work and begin implementation

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

// General AI Solution Proposal
const generateGeneralAIProposal = (requirements) => {
  return `# Fox River AI Project Proposal

![Fox River AI Logo](https://relufox.ai/logo.png)

**Date:** ${new Date().toLocaleDateString()}
**Client:** Demo Client
**Contact:** info@relufox.ai | (630) 940-8837

## Project Overview

Based on your requirements: "${requirements}", we propose a custom AI solution to address your specific business needs.

## Solution Architecture

We recommend building a flexible AI platform using our Self Service Build Wizard to accelerate development with the following components:

1. **User Interface Layer**
   - Web interface with responsive design
   - Mobile compatibility
   - API access for system integration

2. **Application Layer**
   - AWS Lambda for serverless processing
   - API Gateway for secure endpoints
   - Step Functions for complex workflows

3. **AI/ML Layer**
   - Amazon SageMaker for custom model development
   - AWS Bedrock for foundation models
   - Amazon Comprehend for text analysis

4. **Data Layer**
   - Amazon DynamoDB for operational data
   - Amazon S3 for unstructured data storage
   - Amazon RDS for structured data

## Implementation Timeline

| Phase | Duration | Description |
|-------|----------|-------------|
| Discovery | 3 weeks | Requirements gathering and solution definition |
| Design | 4 weeks | Solution architecture and technical design |
| Development | 10 weeks | Implementation of core features and AI components |
| Testing | 3 weeks | Quality assurance and user acceptance testing |
| Deployment | 2 weeks | Production deployment and system integration |

## Cost Estimate

Based on the requirements provided, we estimate the following costs:

### Development Costs
- Discovery & Design: $20,000 - $30,000
- Implementation: $50,000 - $80,000
- Testing & Deployment: $15,000 - $25,000

### Ongoing Costs (Monthly)
- AWS Infrastructure: $800 - $2,000
- Maintenance & Support: $2,500 - $4,000
- Model Updates & Training: $1,000 - $2,500

## Self Service Build Wizard

Our Self Service Build Wizard significantly reduces development costs and accelerates project delivery by:
- Automating AI infrastructure setup and configuration
- Providing pre-built AI components and model templates
- Streamlining integration with AWS AI/ML services
- Reducing manual coding requirements by up to 55%
- Enabling rapid prototyping and iteration of AI models

## Assumptions

This proposal is based on the following assumptions:
- Integration with up to 3 existing systems
- Support for up to 100 concurrent users
- Standard security requirements (no special compliance needs)
- Initial training data provided by client
- Use of our Self Service Build Wizard for accelerated development

## Next Steps

To proceed with this project, we recommend:
1. Schedule a detailed discovery workshop
2. Define success criteria and project scope
3. Sign statement of work
4. Begin design phase

Please contact us at info@relufox.ai to discuss this proposal further.`;
};

/**
 * Mock API call for document processing
 * @param {Object} requestData - Request data
 * @returns {Promise<Object>} Response data
 */
export const processDocument = async (requestData) => {
  // Simulate API delay with safe timeout
  const delay = 1500; // Fixed delay
  await new Promise(resolve => setTimeout(resolve, delay));
  
  // Check if this is a file upload or text message
  if (requestData.fileContent) {
    // Mock file processing response
    return {
      analysis: "I've analyzed your uploaded document. Based on the content, here's a proposal tailored to your requirements.",
      proposal: generateProposalTemplate("Requirements from uploaded document")
    };
  } else if (requestData.message) {
    // Determine the type of project from the message or context
    let projectType = requestData.context?.projectType;
    const lowerMessage = requestData.message.toLowerCase();
    
    if (!projectType) {
      if (lowerMessage.includes('agent') || lowerMessage.includes('autonomous') || 
          lowerMessage.includes('multi-step') || lowerMessage.includes('task planning') || 
          lowerMessage.includes('chat with my data') || lowerMessage.includes('assistant') || 
          (lowerMessage.includes('chatgpt') && lowerMessage.includes('like'))) {
        projectType = 'genai_agent';
      } else if (lowerMessage.includes('document') || lowerMessage.includes('contract') || 
          lowerMessage.includes('pdf') || lowerMessage.includes('extract') || 
          lowerMessage.includes('rag') || lowerMessage.includes('llm') || 
          lowerMessage.includes('nlp')) {
        projectType = 'document_ai';
      } else if (lowerMessage.includes('image') || lowerMessage.includes('vision') || 
                lowerMessage.includes('object detection') || lowerMessage.includes('recognition')) {
        projectType = 'computer_vision';
      } else if (lowerMessage.includes('inventory') || lowerMessage.includes('stock') || lowerMessage.includes('order')) {
        projectType = 'inventory';
      } else if (lowerMessage.includes('chatbot') || lowerMessage.includes('chat')) {
        projectType = 'chatbot';
      } else if (lowerMessage.includes('data') || lowerMessage.includes('analytics')) {
        projectType = 'analytics';
      } else if (lowerMessage.includes('recommend') || lowerMessage.includes('suggestion')) {
        projectType = 'recommendation';
      } else {
        projectType = 'general';
      }
    }
    
    // Create a more detailed message by combining the original message with context
    let enhancedMessage = requestData.message;
    
    if (requestData.context) {
      const { skuCount, productCount, warehouseCount, integrationType } = requestData.context;
      
      if (skuCount) {
        enhancedMessage += `\nSKU Count: ${skuCount}`;
      } else if (productCount) {
        enhancedMessage += `\nProduct Count: ${productCount}`;
      }
      
      if (warehouseCount) {
        if (typeof warehouseCount === 'number') {
          enhancedMessage += `\nWarehouses: ${warehouseCount}`;
        } else {
          enhancedMessage += '\nMultiple warehouse locations';
        }
      }
      
      if (integrationType) {
        enhancedMessage += `\nIntegration: ${integrationType}`;
      }
    }
    
    let responseText = "Based on your requirements, I've prepared a proposal";
    
    switch (projectType) {
      case 'genai_agent':
        // Build feature description
        let features = [];
        if (requestData.context?.needsMemory) features.push("persistent memory");
        if (requestData.context?.needsTools) features.push("tool integration");
        if (requestData.context?.needsOrchestration) features.push("workflow orchestration");
        
        const featuresText = features.length > 0 ? 
                           ` with ${features.join(", ")}` : "";
        
        responseText += ` for your custom GenAI agent${featuresText}`;
        break;
      case 'document_ai':
        if (requestData.context?.specializedUseCase === 'legal_nlp') {
          responseText += " for your legal document processing system";
        } else if (requestData.context?.specializedUseCase === 'medical_nlp') {
          responseText += " for your medical document analysis system";
        } else if (requestData.context?.specializedUseCase === 'financial_nlp') {
          responseText += " for your financial document processing system";
        } else {
          responseText += " for your document AI solution";
        }
        break;
      case 'computer_vision':
        if (requestData.context?.specializedUseCase === 'medical_imaging') {
          responseText += " for your medical imaging analysis system";
        } else {
          responseText += " for your computer vision solution";
        }
        break;
      case 'inventory':
        responseText += " for your inventory management system";
        break;
      case 'chatbot':
        responseText += " for your AI chatbot project";
        break;
      case 'analytics':
        responseText += " for your data analytics solution";
        break;
      case 'recommendation':
        responseText += " for your recommendation system";
        break;
      default:
        responseText += " for your AI project";
    }
    
    responseText += ". This proposal includes our Self Service Build Wizard to significantly reduce costs. You can download it as a PDF using the button below.";
    
    // Mock text message response
    return {
      response: responseText,
      proposal: generateProposalTemplate(enhancedMessage)
    };
  }
  
  // Default response if no valid input
  return {
    response: "I couldn't process your request. Please provide project requirements or upload a document.",
    proposal: null
  };
};